import { Component, type ErrorInfo, type ReactNode } from 'react'

/*
 * Calm section-level error boundary for the CEO cockpit. One bad payload field
 * (a null the UI did not expect, a shape drift from the server) must never
 * white-screen the whole morning brief: the failing section swaps to a quiet
 * "couldn't render" card and everything around it keeps working.
 *
 * `resetKey` lets a parent clear the error when the underlying data changes
 * (e.g. a fresh payload arrives) — the boundary re-mounts its children.
 */

export type CeoErrorBoundaryProps = {
  /** Human name of the section, used in the fallback copy and telemetry. */
  section: string
  /** Optional hook for analytics / logging. */
  onError?: (error: Error, info: { section: string; componentStack?: string | null }) => void
  /** Optional page-level reload (e.g. re-fetch the cockpit). */
  onReload?: () => void
  /** Changing this value clears a caught error and re-renders the children. */
  resetKey?: unknown
  /** Render as a plain block (default) or inline (no section chrome). */
  compact?: boolean
  children: ReactNode
}

type CeoErrorBoundaryState = { error: Error | null; resetKey: unknown }

export class CeoErrorBoundary extends Component<CeoErrorBoundaryProps, CeoErrorBoundaryState> {
  state: CeoErrorBoundaryState = { error: null, resetKey: undefined }

  static getDerivedStateFromError(error: Error): Partial<CeoErrorBoundaryState> {
    return { error }
  }

  static getDerivedStateFromProps(
    props: CeoErrorBoundaryProps,
    state: CeoErrorBoundaryState,
  ): Partial<CeoErrorBoundaryState> | null {
    if (state.error && state.resetKey !== props.resetKey) {
      return { error: null, resetKey: props.resetKey }
    }
    if (state.resetKey !== props.resetKey) return { resetKey: props.resetKey }
    return null
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    try {
      this.props.onError?.(error, { section: this.props.section, componentStack: info?.componentStack || null })
    } catch {
      /* never let telemetry throw out of a boundary */
    }
    if (typeof console !== 'undefined' && typeof console.error === 'function') {
      console.error(`[ceo-cockpit] ${this.props.section} failed to render`, error)
    }
  }

  private retry = () => {
    this.setState({ error: null })
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children
    const { section, onReload, compact } = this.props
    return (
      <div
        className={`ceo-not-tracked ceo-section-error${compact ? ' is-compact' : ''}`}
        role="alert"
        data-testid="ceo-section-error"
        data-section={section}
      >
        <h3>{section} couldn’t render</h3>
        <p>
          Something in this morning’s data didn’t match what this section expected. The rest of the cockpit is
          unaffected.
        </p>
        <div className="ceo-not-tracked-ctas">
          <button type="button" onClick={this.retry}>
            Try again
          </button>
          {onReload ? (
            <button type="button" onClick={onReload}>
              Reload
            </button>
          ) : null}
        </div>
      </div>
    )
  }
}

export default CeoErrorBoundary
